import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Header from "../Includes/Header";
import ListPagination from "../../components/ListPagination/ListPagination";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";
import { getCronJobs, getCronRuns } from "../Apihelper/CronLogApi";

const AUTO_REFRESH_MS = 30000;

const HEALTH = {
  ok: { label: "Working", color: "#16a34a" },
  failed: { label: "Failed", color: "#dc2626" },
  missed: { label: "Missed", color: "#ea580c" },
  no_data: { label: "Waiting for first run", color: "#64748b" },
};
const HEALTH_ORDER = ["ok", "failed", "missed", "no_data"];
const HEALTH_RANK = { failed: 0, missed: 1 };

const RUN_STATUS = {
  success: { label: "Success", color: "success", icon: <CheckCircleIcon /> },
  failed: { label: "Failed", color: "error", icon: <ErrorIcon /> },
  skipped: { label: "Skipped", color: "default", icon: <HourglassEmptyIcon /> },
};

const getToken = () => {
  try { return JSON.parse(localStorage.getItem("userdata") || "{}").token; } catch (e) { return null; }
};

const fmtDateTime = (v) => (v ? dayjs(v).format("DD-MM-YYYY HH:mm") : "-");
const fmtDateTimeSec = (v) => (v ? dayjs(v).format("DD-MM-YYYY HH:mm:ss") : "-");

/** 450 -> "450 ms", 1500 -> "1.5 s", 95000 -> "1 min 35 s". */
const fmtDuration = (ms) => {
  if (ms === null || ms === undefined || ms === "") return "-";
  const n = Number(ms);
  if (!Number.isFinite(n)) return "-";
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")} s`;
  const min = Math.floor(n / 60000);
  const sec = Math.round((n % 60000) / 1000);
  return sec ? `${min} min ${sec} s` : `${min} min`;
};

/** 12 -> "12 s", 300 -> "5 min", 7500 -> "2 h 5 min", 200000 -> "2 days". */
const fmtAgo = (seconds) => {
  if (seconds === null || seconds === undefined) return "";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  const d = Math.floor(s / 86400);
  return `${d} day${d === 1 ? "" : "s"}`;
};

function HealthChip({ health }) {
  const h = HEALTH[health] || HEALTH.no_data;
  return (
    <Chip
      size="small"
      label={h.label}
      icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: h.color, ml: "8px !important" }} />}
      sx={{ fontWeight: 600, color: h.color, bgcolor: `${h.color}14`, border: `1px solid ${h.color}40`, maxWidth: "100%" }}
    />
  );
}

function RunChip({ status }) {
  const s = RUN_STATUS[status] || { label: status || "-", color: "default" };
  return <Chip size="small" icon={s.icon} label={s.label} color={s.color} sx={{ fontWeight: 600 }} />;
}

const runDetail = (r) => {
  if (r.status === "failed") return r.error || "Failed";
  if (r.status === "skipped") return r.error || "Skipped";
  return r.error || "Completed";
};

/** Heartbeat banner at the top: is the scheduler itself ticking? */
function HeartbeatBanner({ heartbeat, onRefresh, refreshing }) {
  const status = heartbeat?.status || "no_data";
  const cfg = {
    ok: { color: "#16a34a", icon: <CheckCircleIcon />, title: "Cron is running" },
    stopped: { color: "#dc2626", icon: <ErrorIcon />, title: "Cron has stopped" },
    no_data: { color: "#d97706", icon: <HourglassEmptyIcon />, title: "Waiting for the first run" },
  }[status] || { color: "#d97706", icon: <HourglassEmptyIcon />, title: "Waiting for the first run" };

  let sub = "No scheduler run has been recorded yet.";
  if (heartbeat?.last_tick) {
    const at = dayjs(heartbeat.last_tick);
    const when = at.isSame(dayjs(), "day") ? at.format("HH:mm:ss") : at.format("DD-MM-YYYY HH:mm:ss");
    const ago = heartbeat.seconds_ago !== null && heartbeat.seconds_ago !== undefined ? `${fmtAgo(heartbeat.seconds_ago)} ago` : "";
    sub = status === "stopped"
      ? `Last ran ${ago} (${when}). Scheduled jobs are not running.`
      : `Last ran ${ago} (${when})`;
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, mb: 2, border: "1px solid", borderColor: `${cfg.color}55`, background: `linear-gradient(135deg, ${cfg.color}1a 0%, #fff 70%)` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ width: 46, height: 46, borderRadius: 2.5, bgcolor: `${cfg.color}22`, color: cfg.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {cfg.icon}
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>Scheduler</Typography>
          <Typography fontWeight={800} sx={{ color: cfg.color, fontSize: { xs: 18, sm: 20 }, lineHeight: 1.25 }}>{cfg.title}</Typography>
          <Typography variant="body2" color="text.secondary">{sub}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          <Typography variant="caption" color="text.secondary">Auto-refresh every 30 s</Typography>
          <Tooltip title="Refresh now">
            <span>
              <IconButton onClick={onRefresh} disabled={refreshing} sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                {refreshing ? <CircularProgress size={22} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}

/** One job's run history, loaded only when the job is opened. */
function JobHistory({ job, refreshKey, isMobile }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async (silent) => {
    const token = getToken();
    if (!token) { navigate("/"); return; }
    if (!silent) setLoading(true);
    try {
      const res = await getCronRuns(token, { command: job.command, limit: pageSize, index: page * pageSize });
      if (Number(res?.code) === 200) {
        setRows(Array.isArray(res?.data) ? res.data : []);
        setTotal(Number(res?.total_count) || 0);
      } else if (!silent) {
        setRows([]); setTotal(0);
      }
    } catch (e) {
      if (!silent) { setRows([]); setTotal(0); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [job.command, page, pageSize, navigate]);

  // First load and page changes.
  useEffect(() => { load(false); }, [load]);
  // Quiet reload when the page refreshes (skip the key it was opened with).
  const openedKey = useRef(refreshKey);
  useEffect(() => { if (refreshKey !== openedKey.current) load(true); }, [refreshKey]);

  return (
    <Box sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "#fcfcfd" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={26} /></Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography fontWeight={600}>No runs recorded yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            {job.next_run ? `The first run will appear here at ${fmtDateTime(job.next_run)}.` : "The first run will appear here once it runs."}
          </Typography>
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: "grid", gap: 1, p: 1.5 }}>
          {rows.map((r) => (
            <Paper key={r.id} elevation={0} sx={{ p: 1.25, borderRadius: 2, border: "1px solid", borderColor: "divider", borderLeft: `4px solid ${r.status === "failed" ? "#dc2626" : r.status === "success" ? "#16a34a" : "#94a3b8"}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>{fmtDateTimeSec(r.started_at)}</Typography>
                <RunChip status={r.status} />
              </Box>
              <Typography variant="caption" color="text.secondary">Took {fmtDuration(r.duration_ms)}</Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.25, wordBreak: "break-word", color: r.status === "failed" ? "error.main" : "text.secondary" }}>{runDetail(r)}</Typography>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "#1e293b", color: "#fff", fontWeight: 600 } }}>
                <TableCell>Time</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Took</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTimeSec(r.started_at)}</TableCell>
                  <TableCell><RunChip status={r.status} /></TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDuration(r.duration_ms)}</TableCell>
                  <TableCell sx={{ wordBreak: "break-word", color: r.status === "failed" ? "error.main" : "text.primary" }}>{runDetail(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {total > 0 && (
        <ListPagination
          count={total}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => { setPageSize(n); setPage(0); }}
        />
      )}
    </Box>
  );
}

const DESKTOP_COLS = "170px minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr) 28px";

function LastRunText({ lastRun, bare }) {
  const label = bare ? "" : "Last run: ";
  if (!lastRun) return <Typography variant="body2" color="text.secondary">{label}not recorded yet</Typography>;
  const st = RUN_STATUS[lastRun.status]?.label || lastRun.status || "-";
  const stColor = lastRun.status === "failed" ? "error.main" : lastRun.status === "success" ? "success.main" : "text.secondary";
  return (
    <Typography variant="body2" component="div">
      {label}{fmtDateTime(lastRun.started_at)} · took {fmtDuration(lastRun.duration_ms)} ·{" "}
      <Box component="span" sx={{ color: stColor, fontWeight: 600 }}>{st}</Box>
    </Typography>
  );
}

/** One scheduled job: summary row/card that opens its run history. */
function JobRow({ job, refreshKey, isMobile, compact }) {
  const [open, setOpen] = useState(false);
  const h = HEALTH[job.health] || HEALTH.no_data;
  const lastRun = job.last_run;
  const failedError = lastRun && lastRun.status === "failed" && lastRun.error;
  const failures = Number(job.failures_24h) || 0;

  const errorLine = failedError ? (
    <Typography variant="caption" sx={{ display: "block", color: "error.main", mt: 0.5, wordBreak: "break-word" }}>
      {lastRun.error}
    </Typography>
  ) : null;

  const counts = (
    <Typography variant="body2" color="text.secondary">
      Last 24 h: {Number(job.runs_24h) || 0} runs ·{" "}
      <Box component="span" sx={{ color: failures ? "error.main" : "inherit", fontWeight: failures ? 700 : 400 }}>{failures} failed</Box>
    </Typography>
  );

  return (
    <Paper elevation={0} sx={{ mb: 1.25, borderRadius: 2.5, border: "1px solid", borderColor: open ? h.color : "divider", borderLeft: `4px solid ${h.color}`, overflow: "hidden", transition: "border-color .2s" }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        role="button"
        aria-expanded={open}
        sx={{ p: { xs: 1.5, md: 1.5 }, cursor: "pointer", "&:hover": { bgcolor: `${h.color}0a` } }}
      >
        {isMobile ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <HealthChip health={job.health} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto", textAlign: "right" }}>{job.schedule_label || job.expression || ""}</Typography>
              <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: "text.secondary", flexShrink: 0 }} />
            </Box>
            <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>{job.name || job.command}</Typography>
            {job.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{job.description}</Typography>}
            <LastRunText lastRun={lastRun} />
            {errorLine}
            <Typography variant="body2" color="text.secondary">Next run: {fmtDateTime(job.next_run)}</Typography>
            {counts}
          </Box>
        ) : compact ? (
          <Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr) 28px", gap: 1.5, alignItems: "center", mb: 1 }}>
              <Box><HealthChip health={job.health} /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>{job.name || job.command}</Typography>
                {job.description && <Typography variant="body2" color="text.secondary">{job.description}</Typography>}
              </Box>
              <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: "text.secondary" }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, pl: "162px", pr: "40px" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Schedule</Typography>
                <Typography variant="body2" fontWeight={600}>{job.schedule_label || "-"}</Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Last run</Typography>
                <LastRunText lastRun={lastRun} bare />
                {errorLine}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Next run</Typography>
                <Typography variant="body2">{fmtDateTime(job.next_run)}</Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Last 24 h</Typography>
                <Typography variant="body2" color="text.secondary">
                  {Number(job.runs_24h) || 0} runs ·{" "}
                  <Box component="span" sx={{ color: failures ? "error.main" : "inherit", fontWeight: failures ? 700 : 400 }}>{failures} failed</Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: DESKTOP_COLS, gap: 1.5, alignItems: "center" }}>
            <Box sx={{ minWidth: 0 }}><HealthChip health={job.health} /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>{job.name || job.command}</Typography>
              {job.description && <Typography variant="body2" color="text.secondary">{job.description}</Typography>}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{job.schedule_label || "-"}</Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <LastRunText lastRun={lastRun} />
              {errorLine}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">Next run: {fmtDateTime(job.next_run)}</Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>{counts}</Box>
            <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: "text.secondary" }} />
          </Box>
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <JobHistory job={job} refreshKey={refreshKey} isMobile={isMobile} />
      </Collapse>
    </Paper>
  );
}

export default function CronLogsPage() {
  const { colors: appColors } = useAppTheme();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const compact = useMediaQuery("(max-width:1599px)"); // too narrow for six columns: stack the details under the name
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [healthFilter, setHealthFilter] = useState("");

  const loadJobs = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate("/"); return; }
    setRefreshing(true);
    try {
      const res = await getCronJobs(token);
      if (Number(res?.code) === 200 && res?.data) {
        setData(res.data);
        setError("");
      } else {
        setError(res?.message || "Could not load the scheduled jobs.");
      }
    } catch (e) {
      setError("Could not reach the server to load the scheduled jobs.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadJobs();
    const timer = setInterval(() => {
      loadJobs();
      setRefreshKey((k) => k + 1);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadJobs]);

  const refresh = () => { loadJobs(); setRefreshKey((k) => k + 1); };

  const summary = data?.summary || {};
  const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
  const ordered = jobs
    .map((j, i) => ({ j, i }))
    .sort((a, b) => ((HEALTH_RANK[a.j.health] ?? 2) - (HEALTH_RANK[b.j.health] ?? 2)) || (a.i - b.i))
    .map((x) => x.j);
  const visible = healthFilter ? ordered.filter((j) => j.health === healthFilter) : ordered;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ mt: { xs: "72px", sm: "80px" }, maxWidth: 1400, mx: "auto" }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 700 }}>Cron Log</Typography>
            <Typography variant="body2" color="text.secondary">
              Is the server running its scheduled jobs? Tap a job to see its recent runs.
            </Typography>
          </Box>

          {loading && !data ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
          ) : (
            <>
              {error && (
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: "1px solid #fecaca", bgcolor: "#fef2f2" }}>
                  <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
                    {error}{data ? " Showing the last loaded data." : ""}
                  </Typography>
                </Paper>
              )}

              <HeartbeatBanner heartbeat={data?.heartbeat} onRefresh={refresh} refreshing={refreshing} />

              {data && (
                <>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {HEALTH_ORDER.map((key) => {
                      const h = HEALTH[key];
                      const active = healthFilter === key;
                      const n = Number(summary[key]) || 0;
                      return (
                        <Chip
                          key={key}
                          onClick={() => setHealthFilter((f) => (f === key ? "" : key))}
                          icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: active ? "#fff" : h.color, ml: "10px !important" }} />}
                          label={`${h.label}: ${n}`}
                          sx={{
                            fontWeight: 600,
                            bgcolor: active ? h.color : "background.paper",
                            color: active ? "#fff" : (n > 0 && (key === "failed" || key === "missed") ? h.color : "text.secondary"),
                            border: "1px solid",
                            borderColor: active ? "transparent" : (n > 0 && (key === "failed" || key === "missed") ? `${h.color}66` : "divider"),
                            "&:hover": { bgcolor: active ? h.color : "action.hover" },
                          }}
                        />
                      );
                    })}
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", ml: 0.5 }}>
                      {Number(summary.total) || jobs.length} jobs in total{healthFilter ? " · tap the chip again to show all" : ""}
                    </Typography>
                  </Box>

                  {!isMobile && !compact && visible.length > 0 && (
                    <Box sx={{ display: "grid", gridTemplateColumns: DESKTOP_COLS, gap: 1.5, pl: "16px", pr: 1.5, pb: 0.75 }}>
                      {["Health", "Job", "Schedule", "Last run", "Next run", "Last 24 h", ""].map((t, i) => (
                        <Typography key={i} variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{t}</Typography>
                      ))}
                    </Box>
                  )}

                  {visible.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                      <Typography fontWeight={600}>
                        {healthFilter ? `No jobs are "${HEALTH[healthFilter].label}" right now.` : "No scheduled jobs found."}
                      </Typography>
                    </Paper>
                  ) : (
                    visible.map((job) => (
                      <JobRow key={job.command} job={job} refreshKey={refreshKey} isMobile={isMobile} compact={compact} />
                    ))
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
